import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'

/**
 * API service layer for Chamber Room Audit Management operations.
 * Base path: /api/secure/cc/support/l3-support/chamber-rooms/audit
 * 
 * All endpoints require authenticated support users with:
 * - ROLE_THERAPY_SUP_L3: full access
 * - ROLE_CLINIC_SUCCESS_ADMIN: full access
 * - ROLE_CLINIC_SUCCESS_MANAGER: access limited to assigned clinic brands/locations
 */

const BASE_PATH = '/api/secure/cc/support/l3-support/chamber-rooms/audit'

/**
 * Error code mappings for user-friendly messages
 */
export const AUDIT_ERROR_CODES = {
  455: 'Request not found or no longer pending.',
  456: 'Could not save approval decision. Try again.',
  4061: 'You are not allowed to access this clinic.',
  4243: 'Missing required fields.',
  4244: 'Auditor is not eligible for this chamber room.',
  4351: 'No active session in this room.',
  4354: 'Invalid room/channel.',
  4355: 'Session channel is not active.',
  4356: 'Mentor mismatch.',
  4357: 'You are not authorized to audit this session.',
  8513: 'Could not generate join tokens. Retry.',
}

/**
 * Request status values
 */
export const REQUEST_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'DENIED',
  'DELETED',
  'EXPIRED',
  'CANCELLED',
]

export const chamberRoomAuditAPI = {
  /**
   * Search audit requests with filters
   * @param {Object} filters - Filter parameters
   * @param {string[]} filters.chamberRoomIds - Filter by chamber room IDs
   * @param {string[]} filters.mentorToAuditIds - Filter by mentor IDs being audited
   * @param {string[]} filters.auditorMentorIds - Filter by auditor mentor IDs
   * @param {string[]} filters.requestStatuses - Filter by request statuses
   * @param {string} filters.expiryAfter - ISO-8601 date-time (UTC)
   * @param {string} filters.expiryBefore - ISO-8601 date-time (UTC)
   * @param {number} filters.page - Page number (default: 0)
   * @param {number} filters.size - Page size (default: 20)
   * @param {string} filters.sort - Sort string (e.g., "createdTime,desc")
   * @returns {Promise} Paginated response with audit requests
   */
  searchAuditRequests: async (filters = {}) => {
    const {
      chamberRoomIds = [],
      mentorToAuditIds = [],
      auditorMentorIds = [],
      requestStatuses = [],
      expiryAfter,
      expiryBefore,
      page = 0,
      size = 20,
      sort = 'createdTime,desc',
    } = filters

    const params = {
      page,
      size,
      sort,
    }

    // Add repeatable parameters
    chamberRoomIds.forEach((id) => {
      if (id?.trim()) {
        params.chamberRoomIds = params.chamberRoomIds || []
        params.chamberRoomIds.push(id.trim())
      }
    })

    mentorToAuditIds.forEach((id) => {
      if (id?.trim()) {
        params.mentorToAuditIds = params.mentorToAuditIds || []
        params.mentorToAuditIds.push(id.trim())
      }
    })

    auditorMentorIds.forEach((id) => {
      if (id?.trim()) {
        params.auditorMentorIds = params.auditorMentorIds || []
        params.auditorMentorIds.push(id.trim())
      }
    })

    requestStatuses.forEach((status) => {
      if (status) {
        params.requestStatuses = params.requestStatuses || []
        params.requestStatuses.push(status)
      }
    })

    if (expiryAfter) {
      params.expiryAfter = expiryAfter
    }

    if (expiryBefore) {
      params.expiryBefore = expiryBefore
    }

    return await getSPDataWithParams(`${BASE_PATH}/auditor-requests/search`, params)
  },

  /**
   * Approve or reject audit access
   * @param {string} auditEnableRequestId - The audit request ID
   * @param {string} approvalStatus - 'APPROVED' or 'REJECTED'
   * @param {string} approvalComment - Optional comment
   * @param {string} approvalExpiry - Optional ISO-8601 date-time (UTC). If omitted, access is permanent.
   * @returns {Promise} Updated audit request
   */
  approveOrRejectAuditAccess: async (auditEnableRequestId, approvalStatus, approvalComment = '', approvalExpiry = null) => {
    const params = {
      auditEnableRequestId,
      approvalStatus,
    }

    if (approvalComment?.trim()) {
      params.approvalComment = approvalComment.trim()
    }

    if (approvalExpiry) {
      params.approvalExpiry = approvalExpiry
    }

    return await postSPDataWithParams(`${BASE_PATH}/audit-access/approve`, {}, params)
  },

  /**
   * Prune expired temporary audit access (global)
   * Prunes expired entries across all chamber rooms
   * @returns {Promise} Response with pruned count and timestamp
   */
  pruneExpiredAccess: async () => {
    return await postSPDataWithParams(`${BASE_PATH}/audit-access/prune`, {})
  },

  /**
   * Prune expired temporary audit access for a specific room
   * @param {string} roomId - Chamber room ID
   * @returns {Promise} Response with pruned count and timestamp
   */
  pruneExpiredAccessForRoom: async (roomId) => {
    return await postSPDataWithParams(`${BASE_PATH}/audit-access/prune/room`, {}, { roomId })
  },
}
